import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth } from '../config/firebase';
import PhoneAuth from './PhoneAuth';
import RoleSelection from './RoleSelection';
import AuthSelection from './AuthSelection';
import FaqSection from './FaqSection';
import BrandMark from './common/BrandMark';
import { CheckCircle, ChevronRight, ChevronLeft, X, Maximize2, ArrowRight } from 'lucide-react';


const PlayStoreIcon = ({ className }) => (
  <svg viewBox="0 0 16 16" className={className} fill="currentColor">
    <path d="M14.222 9.374c1.037-.61 1.037-2.137 0-2.748L11.528 5.04 8.32 8l3.207 2.96zm-3.595 2.116L7.583 8.68 1.03 14.73c.201 1.029 1.36 1.61 2.303 1.055zM1 13.396V2.603L6.846 8zM1.03 1.27l6.553 6.05 3.044-2.81L3.333.215C2.39-.341 1.231.24 1.03 1.27" />
  </svg>
);

const AppStoreIcon = ({ className }) => (
  <svg viewBox="0 0 384 512" className={className} fill="currentColor">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

const APP_STORE_URL = 'https://apps.apple.com/in/app/skillvend/id6800308468';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.skillvend.app';

const STORES = [
  { id: 'ios', caption: 'Download on the', label: 'App Store', url: APP_STORE_URL, Icon: AppStoreIcon },
  { id: 'android', caption: 'Get it on', label: 'Google Play', url: PLAY_STORE_URL, Icon: PlayStoreIcon },
];

// Best-effort device detection so we can surface the right store first
const getPreferredStore = () => {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent || navigator.vendor || '';
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  // iPadOS 13+ reports itself as a Mac, but exposes touch events
  if (/Macintosh/.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return null;
};

const BRAND_COLORS = {
  primary: '#1a1a1a',
  secondary: '#7C3AED',
  accent: '#C9A961',
  dark: '#000000',
  light: '#F5F5F0',
  lightGray: '#E8E8E3',
  mediumGray: '#A0A09C',
  graphite: '#4A4A46',
  // #C9A961 only clears WCAG AA on black (9.3:1); on bone it is 2.1:1
  accentDeep: '#7F6224',
  // mediumGray is 2.4:1 on bone and is safe on black only. Light surfaces that
  // need a third step down from graphite use this instead — 5.3:1 on bone.
  pencil: '#6E6E67',
  // Muted text that still clears AA on black
  mutedOnDark: '#B3B3AD',
  subtleOnDark: '#A8A8A2',
};

// Money that has actually been earned reads in ledger green, the same ink the
// app uses when a day is closed out.
const LEDGER_GREEN = '#1F6B4F';

const HAIRLINE_GOLD = 'rgba(201,169,97,0.32)';
const HAIRLINE_WHITE = 'rgba(255,255,255,0.12)';

const StoreBadge = ({ store, variant = 'solid', className = '' }) => {
  const { Icon } = store;
  const variantClasses = variant === 'solid'
    ? 'bg-white text-black hover:bg-[#C9A961]'
    : 'border border-white/25 text-white hover:border-[#C9A961] hover:text-[#C9A961]';

  return (
    <a
      href={store.url}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-3 px-6 py-3.5 transition-colors duration-200 group ${variantClasses} ${className}`}
    >
      <Icon className="w-5 h-5 flex-shrink-0" />
      <span className="text-left leading-tight">
        <span className="font-ledger block text-[0.62rem] tracking-[0.15em] uppercase opacity-75">{store.caption}</span>
        <span className="block text-sm font-light tracking-[0.15em] mt-0.5">{store.label}</span>
      </span>
    </a>
  );
};

/* ------------------------------------------------------------------
   Objects carried over from the SkillVend app.

   The site could describe what the product does; it could not show the
   thing the product actually makes. These are the app's own components
   rendered for the web, so a visitor sees the artifact before they
   download anything — and so the two surfaces read as one product.
   ------------------------------------------------------------------ */

/** Status as a struck rubber stamp, the way a closed day is marked on a record. */
const Stamp = ({ children, tone = BRAND_COLORS.accentDeep, struck = false, className = '' }) => (
  <span
    className={`font-ledger inline-block uppercase whitespace-nowrap ${className}`}
    style={{
      color: tone,
      border: `${struck ? 2 : 1}px solid ${tone}`,
      padding: struck ? '4px 12px' : '3px 9px',
      fontSize: struck ? '0.72rem' : '0.62rem',
      letterSpacing: '0.14em',
      transform: struck ? 'rotate(-3deg)' : 'none',
    }}
  >
    {children}
  </span>
);

/** The tear line between a record's date stub and its body. */
const Perforation = ({ dark = false }) => (
  <span
    aria-hidden="true"
    className="block w-px flex-none self-stretch"
    style={{
      backgroundImage: `repeating-linear-gradient(to bottom, ${dark ? 'rgba(255,255,255,0.35)' : BRAND_COLORS.lightGray} 0 4px, transparent 4px 9px)`,
    }}
  />
);

/** The date block a register is read down the left edge by. */
const Stub = ({ day, date, month, filled = false }) => (
  <span
    className="flex flex-col items-center justify-center flex-none px-4 py-4"
    style={{ backgroundColor: filled ? BRAND_COLORS.accent : '#FBFBF8', minWidth: '4.25rem' }}
  >
    <span className="font-ledger text-[0.58rem] uppercase tracking-[0.14em]" style={{ color: filled ? BRAND_COLORS.primary : BRAND_COLORS.graphite }}>{day}</span>
    <span className="font-ledger text-xl leading-none my-0.5" style={{ color: BRAND_COLORS.primary }}>{date}</span>
    <span className="font-ledger text-[0.58rem] uppercase tracking-[0.14em]" style={{ color: filled ? BRAND_COLORS.primary : BRAND_COLORS.graphite }}>{month}</span>
  </span>
);

/**
 * A page from a SkillVender's record.
 *
 * The top entry is a finished day, stamped. The ones under it are the
 * days before, and the rule above the total is doing the real work: it
 * says everything above adds up to this. That is the whole promise of
 * the product in one object, which is why it leads the page.
 */
const SpecimenRecord = () => (
  <figure className="m-0">
    <div className="bg-white" style={{ border: `1px solid ${BRAND_COLORS.lightGray}` }}>
      <div style={{ height: 5, backgroundColor: BRAND_COLORS.accent }} />

      <div className="flex items-stretch" style={{ borderBottom: `1px solid ${BRAND_COLORS.lightGray}` }}>
        <Stub day="Sat" date="12" month="Jul" filled />
        <Perforation />
        <div className="flex-1 min-w-0 px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-base font-medium tracking-wide truncate" style={{ color: BRAND_COLORS.primary }}>Barista</p>
              <p className="text-sm font-light truncate" style={{ color: BRAND_COLORS.graphite }}>Elite Cafés, Indiranagar</p>
            </div>
            <Stamp struck tone={LEDGER_GREEN}>Worked</Stamp>
          </div>
          <p className="font-ledger mt-3 text-[0.78rem]" style={{ color: BRAND_COLORS.graphite }}>
            8h 10m on site
            <span className="ml-4" style={{ color: LEDGER_GREEN }}>₹1,000</span>
          </p>
        </div>
      </div>

      {SPECIMEN_ENTRIES.map((entry) => (
        <div key={entry.date} className="flex items-stretch" style={{ borderBottom: `1px solid ${BRAND_COLORS.lightGray}` }}>
          <Stub day={entry.day} date={entry.date} month={entry.month} />
          <Perforation />
          <div className="flex-1 min-w-0 px-5 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate" style={{ color: BRAND_COLORS.primary }}>{entry.role}</p>
              <p className="text-xs font-light truncate" style={{ color: BRAND_COLORS.graphite }}>{entry.venue}</p>
            </div>
            <span className="font-ledger text-[0.78rem] flex-none" style={{ color: BRAND_COLORS.graphite }}>{entry.pay}</span>
          </div>
        </div>
      ))}

      <div className="flex items-baseline px-5 py-4" style={{ borderTop: `2px solid ${BRAND_COLORS.primary}` }}>
        <span className="font-ledger text-[0.68rem] uppercase tracking-[0.16em]" style={{ color: BRAND_COLORS.primary }}>Three days in July</span>
        <span className="flex-1 mx-3 overflow-hidden whitespace-nowrap font-ledger text-xs" style={{ color: BRAND_COLORS.lightGray, letterSpacing: '2px' }}>
          ....................................................
        </span>
        <span className="font-ledger text-lg" style={{ color: BRAND_COLORS.primary }}>₹3,050</span>
      </div>
    </div>

    <figcaption className="font-ledger mt-3 text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: BRAND_COLORS.subtleOnDark }}>
      Specimen record · illustrative figures
    </figcaption>
  </figure>
);

/** The four beats a shift is punched through. */
const PunchStrip = ({ done = 2 }) => (
  <div>
    <div className="flex gap-1">
      {SHIFT_BEATS.map((beat, i) => {
        const isDone = i < done;
        const isNext = i === done;
        return (
          <span
            key={beat}
            className="flex-1 grid place-items-center"
            style={{
              height: 30,
              backgroundColor: isDone ? BRAND_COLORS.primary : isNext ? '#FBF6E8' : '#FFFFFF',
              border: isNext ? `2px solid ${BRAND_COLORS.accentDeep}` : `1px solid ${BRAND_COLORS.lightGray}`,
            }}
          >
            {isDone && <CheckCircle className="w-3.5 h-3.5" style={{ color: BRAND_COLORS.accent }} />}
          </span>
        );
      })}
    </div>
    <div className="flex gap-1 mt-2">
      {SHIFT_BEATS.map((beat, i) => (
        <span
          key={beat}
          className="flex-1 font-ledger text-[0.58rem] uppercase tracking-[0.12em]"
          style={{ color: i === done ? BRAND_COLORS.accentDeep : i < done ? BRAND_COLORS.graphite : BRAND_COLORS.pencil }}
        >
          {beat}
        </span>
      ))}
    </div>
  </div>
);

/**
 * The code slip.
 *
 * Four digits read aloud are the moment a day officially opens and
 * closes. It is the mechanism that makes the hours on a record
 * trustworthy for both sides, and it was the one thing the site never
 * explained.
 */
const CodeSlip = ({ label, digits, note, tone }) => (
  <div className="bg-white flex-1 min-w-[15rem]" style={{ border: `2px solid ${tone}`, padding: '0.6rem 1.15rem 1.15rem' }}>
    <div className="flex justify-between px-0.5 pb-3" aria-hidden="true">
      {Array.from({ length: 9 }).map((_, i) => (
        <span
          key={i}
          className="block rounded-full"
          style={{ width: 8, height: 8, backgroundColor: BRAND_COLORS.light, border: `1px solid ${BRAND_COLORS.lightGray}` }}
        />
      ))}
    </div>
    <p className="font-ledger text-[0.62rem] uppercase tracking-[0.16em]" style={{ color: tone }}>{label}</p>
    <p className="font-ledger mt-1" style={{ color: tone, fontSize: '2rem', letterSpacing: '0.22em' }}>{digits}</p>
    <p className="text-sm font-light mt-1" style={{ color: BRAND_COLORS.graphite }}>{note}</p>
  </div>
);

// Earlier entries on the specimen record. Roles and venues are drawn from the
// same list the gallery shows, so nothing here is invented twice.
const SPECIMEN_ENTRIES = [
  { day: 'Fri', date: '11', month: 'Jul', role: 'Retail Associate', venue: 'Supermarket Chains', pay: '₹950' },
  { day: 'Sun', date: '06', month: 'Jul', role: 'Event Volunteer', venue: 'Marathons & Public Events', pay: '₹1,100' },
];

// The four states a shift is punched through, in the app's own words.
const SHIFT_BEATS = ['Accepted', 'Reached', 'Open', 'Closed'];

const GALLERY_IMAGES = [
  { id: 1, title: 'Barista & Coffee Operations', role: 'Barista', venue: 'Elite Cafés', src: '/Gallary/Cafe1.png', category: 'hospitality' },
  { id: 2, title: '5-Star Culinary Team', role: 'Chef de Partie', venue: 'Luxury Hotels', src: '/Gallary/5 Star Hotel.png', category: 'hospitality' },
  { id: 3, title: 'Front Desk Hospitality', role: 'Guest Relations Associate', venue: 'Luxury Resorts', src: '/Gallary/Greeting 2.png', category: 'hospitality' },
  { id: 4, title: 'Pharmacy Operations', role: 'Pharmacy Assistant', venue: 'Retail Pharmacies', src: '/Gallary/Pharmacy 1.png', category: 'retail' },
  { id: 5, title: 'Supermarket Operations', role: 'Retail Associate', venue: 'Supermarket Chains', src: '/Gallary/Retail 2.png', category: 'retail' },
  { id: 6, title: 'Apparel Merchandising', role: 'Store Associate', venue: 'Lifestyle Retailers', src: '/Gallary/Retail 1.png', category: 'retail' },
  { id: 7, title: 'Cinema Guest Experience', role: 'Multiplex Associate', venue: 'Multiplex Theatres', src: '/Gallary/Multiplex.png', category: 'entertainment' },
  { id: 8, title: 'Event Logistics Support', role: 'Event Volunteer', venue: 'Marathons & Public Events', src: '/Gallary/Marathon.png', category: 'entertainment' },
  { id: 9, title: 'Front Desk Reception', role: 'Receptionist', venue: 'Boutique Hotels', src: '/Gallary/Front Desk.png', category: 'hospitality' },
  { id: 10, title: 'Hotel Guest Welcome', role: 'Lobby Host', venue: 'Premium Hotels', src: '/Gallary/Greeting 3.png', category: 'hospitality' },
  { id: 11, title: 'Supermarket Shelf Management', role: 'Stock Associate', venue: 'Grocery Outlets', src: '/Gallary/Retail 3.png', category: 'retail' },
  { id: 12, title: 'Concierge Desk Support', role: 'Concierge Associate', venue: 'Premium Resorts', src: '/Gallary/Reception .png', category: 'hospitality' },
];

const CATEGORIES = [
  { id: 'all', label: 'All fields' },
  { id: 'hospitality', label: 'Hospitality & culinary' },
  { id: 'retail', label: 'Retail & pharmacy' },
  { id: 'entertainment', label: 'Events & entertainment' },
];

const NAV_LINKS = [
  { href: '#skillvenders', label: 'SkillVenders' },
  { href: '#how', label: 'How it works' },
  { href: '#employers', label: 'Employers' },
  { href: '#gallery', label: 'In action' },
  { href: '/jobs', label: 'Jobs' },
  { href: '#faq', label: 'FAQ' },
];


const TRUST_FACTS = [
  { label: 'No fee, ever', detail: 'SkillVenders pay nothing to register, apply or be placed.' },
  { label: 'Verified people', detail: 'Every profile is built on a verified mobile number and checked documents.' },
  { label: 'Paid per day worked', detail: 'Days present times the published rate. No commission on your payout.' },
  { label: 'Certificate on completion', detail: 'Finish the term you committed to and the company certifies it.' },
];

// A real sequence: the order every SkillVender actually moves through
const STEPS = [
  {
    n: '01',
    title: 'Register and get verified',
    body: 'Create your profile in the app with your mobile number, add your documents, and we verify you before any employer sees you.',
  },
  {
    n: '02',
    title: 'Choose work near you',
    body: 'Opportunities are sorted by distance from where you stay. Before you apply, you can see the role, venue, dates, timings and what the shift pays.',
  },
  {
    n: '03',
    title: 'Work the shift',
    body: 'Report to the venue, check in and out on the platform, and your attendance is recorded as you go.',
  },
  {
    n: '04',
    title: 'Get paid',
    body: 'Days present multiplied by the published rate, paid into your bank account. SkillVend takes no cut of it.',
  },
];

const EMPLOYER_POINTS = [
  'Staff a rush hour, a weekend or a one-off event without adding headcount',
  'Pay only for the hours worked — no salaries, no overheads',
  'Post a requirement in minutes and reach verified people who are already nearby',
  'Scale a floor up for peak season and back down when it passes',
  'See people work before you hire them permanently',
];

// Read off the day itself: what the codes actually govern.
const DAY_FACTS = [
  { value: '2', label: 'Codes per day, one each end' },
  { value: '0', label: 'Hours logged without both sides' },
  { value: 'Per day', label: 'How the rate is published and paid' },
];

// The shift board reads from the same roles shown in the gallery
const BOARD_ROWS = GALLERY_IMAGES.map((img) => ({ role: img.role, venue: img.venue }));

const HERO_STATS = [
  { k: '18+', v: 'For shift work · 17+ to intern' },
  { k: 'Zero fees', v: 'What joining costs you' },
  { k: 'iOS · Android', v: 'Where SkillVend runs' },
];

const LandingPage = ({ user, userProfile }) => {
  const [authStep, setAuthStep] = useState('landing');
  const [selectedRole, setSelectedRole] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [showStoreDialog, setShowStoreDialog] = useState(false);
  const [preferredStore] = useState(getPreferredStore);

  const navigate = useNavigate();
  const pageRef = useRef(null);

  // Surface the store matching the visitor's device first
  const orderedStores = preferredStore === 'android' ? [...STORES].reverse() : STORES;

  const filteredImages = activeCategory === 'all'
    ? GALLERY_IMAGES
    : GALLERY_IMAGES.filter(img => img.category === activeCategory);

  // Sections rise into place once, as they are reached
  useEffect(() => {
    const nodes = pageRef.current?.querySelectorAll('[data-reveal]');
    if (!nodes || !nodes.length) return undefined;

    const revealAll = () => nodes.forEach((node) => node.classList.add('is-visible'));

    // Without an observer the page must still be readable, not blank
    if (typeof IntersectionObserver === 'undefined') {
      revealAll();
      return undefined;
    }

    // Content is only hidden once we know something will reveal it again
    pageRef.current.classList.add('sv-reveal-ready');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );

    nodes.forEach((node) => observer.observe(node));

    // Safety net: if nothing has reported in after three seconds, show everything
    const failsafe = window.setTimeout(() => {
      if (!document.querySelector('[data-reveal].is-visible')) revealAll();
    }, 3000);

    return () => {
      window.clearTimeout(failsafe);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowRight') {
        setLightboxIndex((prev) => (prev + 1) % filteredImages.length);
      }
      if (e.key === 'ArrowLeft') {
        setLightboxIndex((prev) => (prev - 1 + filteredImages.length) % filteredImages.length);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, filteredImages.length]);


  useEffect(() => {
    if (!showStoreDialog) return undefined;

    const handleEscape = (e) => {
      if (e.key === 'Escape') setShowStoreDialog(false);
    };
    const previousOverflow = document.body.style.overflow;

    window.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [showStoreDialog]);

  useEffect(() => {
    // Check for invite code in URL
    const searchParams = new URLSearchParams(window.location.search);
    const inviteCode = searchParams.get('invite');

    if (inviteCode) {
      // Store for use during onboarding
      sessionStorage.setItem('inviteCode', inviteCode);
      // Auto-open sign up (optional, but good UX)
      if (!user) {
        setShowAuthModal(true);
        setAuthStep('authSelection');
      }
    }

    if (user && userProfile) {
      if (userProfile.role === 'professional' && userProfile.onBoardingComplete) {
        navigate('/professional/home');
      } else if (userProfile.role === 'employer' && userProfile.onBoardingComplete) {
        navigate('/employer/home');
      }
    }
  }, [user, userProfile, navigate]);

  const handleAuthTypeSelection = (type) => {
    setIsSignUp(type === 'signup');
    if (type === 'signup') {
      setAuthStep('roleSelection');
    } else {
      setAuthStep('auth');
      setSelectedRole(null);
    }
  };

  const handleRoleSelection = (role) => {
    setSelectedRole(role);
    setAuthStep('auth');
  };

  const handleAuthSuccess = async () => {
    setShowAuthModal(false);
    // Navigate to appropriate onboarding route based on selected role
    if (selectedRole === 'professional') {
      navigate('/onboarding/professional');
    } else if (selectedRole === 'employer') {
      navigate('/onboarding/employer');
    }
  };

  const handleBackFromAuth = () => {
    if (isSignUp) {
      setAuthStep('roleSelection');
    } else {
      setAuthStep('authSelection');
    }
  };

  return (
    <div ref={pageRef} className="min-h-screen" style={{ backgroundColor: BRAND_COLORS.light }}>
      {/* Store Redirect Dialog - sign in / sign up happens inside the mobile app */}
      {showStoreDialog && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm animate-fade-in"
          role="dialog"
          aria-modal="true"
          aria-labelledby="store-dialog-title"
          onClick={() => setShowStoreDialog(false)}
        >
          <div
            className="relative w-full max-w-md bg-white border"
            style={{ borderColor: BRAND_COLORS.primary }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowStoreDialog(false)}
              aria-label="Close"
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-black transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="px-8 pt-10 pb-8">
              <p className="font-ledger text-[0.65rem] tracking-[0.2em] uppercase mb-4" style={{ color: BRAND_COLORS.accentDeep }}>
                Continue in the app
              </p>
              <h3 id="store-dialog-title" className="text-2xl font-light tracking-wide mb-3" style={{ color: BRAND_COLORS.primary }}>
                Sign in with the SkillVend app
              </h3>
              <p className="text-sm font-light leading-relaxed text-[#57574F]">
                Accounts are created and verified inside our mobile app. Choose your platform below —
                it takes less than a minute.
              </p>

              <div className="mt-8 space-y-3">
                {orderedStores.map((store) => (
                  <a
                    key={store.id}
                    href={store.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setShowStoreDialog(false)}
                    className="flex items-center justify-between w-full px-4 sm:px-5 py-4 border text-[#1a1a1a] transition-colors duration-150 ease-out group hover:bg-[#F5F5F0] hover:border-[#1a1a1a]"
                    style={{ borderColor: BRAND_COLORS.lightGray }}
                  >
                    <span className="flex items-center gap-4">
                      <store.Icon className="w-5 h-5 flex-shrink-0" />
                      <span className="text-left leading-tight">
                        <span className="flex items-center gap-2">
                          <span className="font-ledger text-[0.62rem] uppercase tracking-[0.15em] text-[#57574F]">
                            {store.caption}
                          </span>
                          {preferredStore === store.id && (
                            <span
                              className="font-ledger text-[0.6rem] uppercase tracking-[0.15em] px-1.5 py-0.5 border whitespace-nowrap"
                              style={{ borderColor: BRAND_COLORS.accentDeep, color: BRAND_COLORS.accentDeep }}
                            >
                              Your device
                            </span>
                          )}
                        </span>
                        <span className="block text-sm font-light tracking-[0.15em] whitespace-nowrap mt-0.5">{store.label}</span>
                      </span>
                    </span>
                    <ChevronRight className="w-5 h-5 flex-shrink-0 transition-transform duration-200 group-hover:translate-x-1" />
                  </a>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t space-y-3" style={{ borderColor: BRAND_COLORS.lightGray }}>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: BRAND_COLORS.accentDeep }} />
                  <p className="text-xs font-light text-[#57574F]">Verification by OTP — no passwords</p>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: BRAND_COLORS.accentDeep }} />
                  <p className="text-xs font-light text-[#57574F]">Already have the app? Open SkillVend to sign in</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-lg w-full">
            <button
              onClick={() => {
                setShowAuthModal(false);
                setAuthStep('landing');
                setSelectedRole(null);
                setIsSignUp(false);
              }}
              className="absolute -top-4 -right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors z-10"
              style={{ color: BRAND_COLORS.primary }}
            >
              ✕
            </button>
            {authStep === 'authSelection' && (
              <AuthSelection
                onSelectSignIn={() => handleAuthTypeSelection('signin')}
                onSelectSignUp={() => handleAuthTypeSelection('signup')}
              />
            )}
            {authStep === 'roleSelection' && (
              <RoleSelection onSelectRole={handleRoleSelection} />
            )}
            {authStep === 'auth' && (
              <PhoneAuth
                isSignUp={isSignUp}
                role={selectedRole}
                onSuccess={handleAuthSuccess}
                onBack={handleBackFromAuth}
              />
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-black border-b sticky top-0 z-40" style={{ borderColor: HAIRLINE_GOLD }}>
        {/*
          Everything in this row is whitespace-nowrap, so nothing shrinks — which
          means the row has to be sized so it never needs to. Between 1024 and
          1280 the container is capped at 1024 while both the strapline and the
          full nav are showing, and the overflow pushed the sign-in button clean
          off the right edge.

          Two rules keep it honest: the brand and the call to action are
          flex-none, so neither can ever be squeezed or displaced; the nav is the
          only flexible item, so if this row is ever over-filled again it is a
          section link that clips, never the way in.
        */}
        <div className="container mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between gap-4 xl:gap-6">
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
              if (window.location.hash) {
                window.history.pushState(null, '', '/');
              }
            }}
            className="flex items-center gap-2 md:gap-4 flex-none cursor-pointer group"
            aria-label="SkillVend Home"
          >
            <BrandMark className="w-9 h-9 md:w-10 md:h-10 transition-transform group-hover:scale-105" />
            <div>
              <h1 className="text-sm md:text-base lg:text-lg font-light tracking-widest whitespace-nowrap" style={{ color: '#C9A961' }}>
                SKILL<span className="font-extralight">VEND</span>
              </h1>
              {/* The strapline is the widest thing here and the least load-bearing,
                  so it waits until there is genuinely room beside the nav. */}
              <p className="font-ledger text-[0.6rem] md:text-[0.65rem] tracking-[0.12em] text-[#B3B3AD] mt-1 hidden xl:block whitespace-nowrap uppercase">
                The work marketplace for flexible jobs
              </p>
            </div>
          </a>

          <nav aria-label="Sections" className="hidden lg:flex items-center gap-5 xl:gap-8 min-w-0">
            {NAV_LINKS.map((link) => (
              link.href.startsWith('/') ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className="font-ledger whitespace-nowrap text-[0.65rem] tracking-[0.2em] uppercase text-gray-400 hover:text-[#C9A961] transition-colors duration-200"
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  className="font-ledger whitespace-nowrap text-[0.65rem] tracking-[0.2em] uppercase text-gray-400 hover:text-[#C9A961] transition-colors duration-200"
                >
                  {link.label}
                </a>
              )
            ))}
          </nav>

          {user ? (
            <div className="flex items-center gap-2 md:gap-4 flex-none">
              <span className="text-xs md:text-sm font-light tracking-wide text-gray-400 hidden xl:inline max-w-[14rem] truncate">
                Welcome, {userProfile?.displayName || 'User'}
              </span>
              <button
                onClick={() => auth.signOut()}
                className="flex-none px-3 py-2 md:px-4 md:py-2 border font-light tracking-wide text-xs transition-colors duration-200 hover:bg-white hover:text-black"
                style={{ borderColor: BRAND_COLORS.accent, color: BRAND_COLORS.accent }}
              >
                <span className="hidden sm:inline">SIGN OUT</span>
                <span className="sm:hidden">OUT</span>
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowStoreDialog(true)}
              className="flex-none px-3 py-2 md:px-4 md:py-2.5 font-light tracking-wider text-[0.7rem] md:text-xs whitespace-nowrap transition-colors duration-200 text-center hover:bg-white"
              style={{ backgroundColor: BRAND_COLORS.accent, color: BRAND_COLORS.primary }}
            >
              <span className="hidden sm:inline">SIGN IN / SIGN UP</span>
              <span className="sm:hidden">SIGN IN</span>
            </button>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-black text-white overflow-hidden">
        <div className="container mx-auto px-6 max-w-7xl pt-10 pb-12 md:pt-14 md:pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">

            <div className="lg:col-span-7">
              <p
                className="font-ledger text-[0.6rem] md:text-[0.65rem] tracking-[0.3em] uppercase sv-rise"
                style={{ color: BRAND_COLORS.accent, animationDelay: '80ms' }}
              >
                Flexible work · Verified people · Paid per day
              </p>

              <h2
                className="mt-6 font-extralight leading-[0.95] tracking-tight sv-rise"
                style={{ fontSize: 'clamp(2.75rem, 7vw, 5.5rem)', animationDelay: '180ms' }}
              >
                Empowering
                <span className="block mt-1" style={{ color: BRAND_COLORS.accent }}>Employability.</span>
              </h2>

              <div className="mt-7 h-px w-24 sv-rise" style={{ backgroundColor: BRAND_COLORS.accent, animationDelay: '300ms' }} />

              <p
                className="mt-6 text-lg md:text-xl font-light leading-relaxed text-gray-300 max-w-xl sv-rise"
                style={{ animationDelay: '380ms' }}
              >
                Everyone has something valuable to offer. We make that value visible — and
                pay you for it, one shift at a time.
              </p>

              <div className="mt-8 flex flex-wrap gap-4 sv-rise" style={{ animationDelay: '480ms' }}>
                {orderedStores.map((store) => (
                  <StoreBadge key={store.id} store={store} variant="outline" />
                ))}
              </div>

              <dl
                className="mt-9 grid grid-cols-1 sm:grid-cols-3 border-t sv-rise"
                style={{ borderColor: HAIRLINE_WHITE, animationDelay: '580ms' }}
              >
                {HERO_STATS.map((stat) => (
                  <div key={stat.k} className="py-4 sm:py-5 sm:pr-6 border-b sm:border-b-0" style={{ borderColor: HAIRLINE_WHITE }}>
                    <dt className="text-base md:text-lg font-light tracking-wide" style={{ color: BRAND_COLORS.accent }}>
                      {stat.k}
                    </dt>
                    <dd className="font-ledger mt-2 text-[0.65rem] tracking-[0.12em] uppercase text-[#B3B3AD] leading-relaxed">
                      {stat.v}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>

            {/*
              The thesis of the whole page: not a photograph of work, but the
              record the work leaves behind. It is the same object the app opens
              on, lifted onto the site so the promise is visible before anyone
              downloads anything.
            */}
            <div className="lg:col-span-5">
              <div className="relative sv-rise" style={{ animationDelay: '320ms' }}>
                <div className="absolute -inset-3 border pointer-events-none hidden lg:block" style={{ borderColor: HAIRLINE_GOLD }} />
                <SpecimenRecord />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* The shift board — roles moving through SkillVend right now */}
      <section
        className="sv-board bg-black border-t border-b overflow-hidden"
        style={{ borderColor: HAIRLINE_GOLD }}
        aria-label="Roles available on SkillVend"
      >
        <div className="sv-ticker py-4">
          {[0, 1].map((pass) => (
            <div key={pass} className="flex shrink-0" aria-hidden={pass === 1}>
              {BOARD_ROWS.map((row) => (
                <span key={pass + row.role + row.venue} className="flex items-center gap-4 px-8 whitespace-nowrap">
                  <span className="font-ledger text-[0.7rem] tracking-[0.18em] uppercase text-white">{row.role}</span>
                  <span className="w-1 h-1" style={{ backgroundColor: BRAND_COLORS.accent }} />
                  <span className="font-ledger text-[0.7rem] tracking-[0.18em] uppercase text-[#B3B3AD]">{row.venue}</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* Chapter one — SkillVenders */}
      <section id="skillvenders" className="anchor-target" style={{ backgroundColor: BRAND_COLORS.light }}>
        <div className="container mx-auto px-6 max-w-7xl py-14 md:py-20">

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-reveal>
            <div className="lg:col-span-5">
              <p className="font-ledger text-[0.65rem] tracking-[0.22em] uppercase" style={{ color: BRAND_COLORS.secondary }}>
                For SkillVenders
              </p>
              <h3
                className="mt-6 font-light leading-[1.05] tracking-tight"
                style={{ fontSize: 'clamp(2rem, 4vw, 3.25rem)', color: BRAND_COLORS.primary }}
              >
                Turn free time
                <span className="block mt-1" style={{ color: BRAND_COLORS.secondary }}>into income.</span>
              </h3>
            </div>
            <div className="lg:col-span-7 lg:pt-4">
              <p className="text-lg md:text-xl font-light leading-relaxed" style={{ color: BRAND_COLORS.graphite }}>
                Work when you choose, close to where you stay, for brands people recognise.
                If you are 18 or older and have hours to spare, you already qualify — no degree,
                no experience, no fee. College students can take an internship from 17.
              </p>
              <button
                onClick={() => setShowStoreDialog(true)}
                className="group mt-8 inline-flex items-center gap-3 border-b pb-1 text-xs font-light tracking-[0.2em] uppercase transition-colors duration-200 hover:text-black"
                style={{ color: BRAND_COLORS.primary, borderColor: BRAND_COLORS.accentDeep }}
              >
                Start in the app
                <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
              </button>
            </div>
          </div>

          {/* Four facts, set as a ledger rather than cards */}
          <dl className="mt-11 md:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" data-reveal>
            {TRUST_FACTS.map((fact) => (
              <div
                key={fact.label}
                className="py-7 sm:pr-8 lg:px-8 border-t lg:border-t-0 lg:border-l lg:first:border-l-0 lg:first:pl-0 lg:last:pr-0"
                style={{ borderColor: BRAND_COLORS.lightGray }}
              >
                <dt>
                  <Stamp>{fact.label}</Stamp>
                </dt>
                <dd className="mt-4 text-sm font-light leading-[1.8]" style={{ color: BRAND_COLORS.graphite }}>
                  {fact.detail}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* How it works — a real sequence, so the numbering earns its place */}
      <section id="how" className="anchor-target bg-white border-t border-b" style={{ borderColor: BRAND_COLORS.lightGray }}>
        <div className="container mx-auto px-6 max-w-7xl py-14 md:py-20">
          <div className="max-w-2xl" data-reveal>
            <p className="font-ledger text-[0.65rem] tracking-[0.22em] uppercase" style={{ color: BRAND_COLORS.accentDeep }}>
              How it works
            </p>
            <h3
              className="mt-6 font-light leading-[1.1] tracking-tight"
              style={{ fontSize: 'clamp(1.9rem, 3.6vw, 2.85rem)', color: BRAND_COLORS.primary }}
            >
              Four steps from download to payout.
            </h3>
          </div>

          <ol className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-y-10 lg:gap-y-0">
            {STEPS.map((step, i) => (
              <li
                key={step.n}
                data-reveal
                style={{ transitionDelay: (i * 90) + 'ms', borderColor: BRAND_COLORS.lightGray }}
                className="lg:px-8 lg:border-l lg:first:border-l-0 lg:first:pl-0 lg:last:pr-0"
              >
                <span className="font-ledger block text-[0.8rem] tracking-[0.25em]" style={{ color: BRAND_COLORS.accentDeep }}>
                  {step.n}
                </span>
                <h4 className="mt-5 text-xl font-light tracking-wide" style={{ color: BRAND_COLORS.primary }}>
                  {step.title}
                </h4>
                <p className="mt-4 text-sm font-light leading-[1.85] max-w-sm" style={{ color: BRAND_COLORS.graphite }}>
                  {step.body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/*
        A day on the floor.

        The four-digit handshake is the mechanism the whole product rests on —
        it is what makes the hours on a record something both sides can trust —
        and the site had never once mentioned it. Showing the actual slips does
        more for credibility than another paragraph about verification.
      */}
      <section id="day" className="anchor-target" style={{ backgroundColor: BRAND_COLORS.light }}>
        <div className="container mx-auto px-6 max-w-7xl py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">

            <div className="lg:col-span-5" data-reveal>
              <p className="font-ledger text-[0.65rem] tracking-[0.22em] uppercase" style={{ color: BRAND_COLORS.accentDeep }}>
                A day on the floor
              </p>
              <h3
                className="mt-6 font-light leading-[1.05] tracking-tight"
                style={{ fontSize: 'clamp(1.9rem, 3.6vw, 2.85rem)', color: BRAND_COLORS.primary }}
              >
                Four digits open it.
                <span className="block mt-1" style={{ color: BRAND_COLORS.accentDeep }}>Four close it.</span>
              </h3>
              <p className="mt-6 text-base md:text-lg font-light leading-relaxed" style={{ color: BRAND_COLORS.graphite }}>
                You reach the venue and mark yourself in. The employer reads you a code that
                opens your shift, and another when it ends. Neither side can log hours the
                other did not agree to — which is exactly why the record is worth something.
              </p>

              <dl className="mt-9">
                {DAY_FACTS.map((fact) => (
                  <div
                    key={fact.label}
                    className="flex items-baseline gap-4 py-4 border-t last:border-b"
                    style={{ borderColor: BRAND_COLORS.lightGray }}
                  >
                    <dt className="font-ledger text-base flex-none" style={{ color: BRAND_COLORS.primary, minWidth: '4.5rem' }}>
                      {fact.value}
                    </dt>
                    <dd className="font-ledger text-[0.62rem] tracking-[0.12em] uppercase leading-relaxed" style={{ color: BRAND_COLORS.graphite }}>
                      {fact.label}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>

            {/*
              The arc of the day sits above the two moments inside it, because
              that is the order they happen in: you are rostered, you reach the
              site, then a code opens the shift and a code closes it.
            */}
            <div className="lg:col-span-7 lg:pt-2" data-reveal>
              <PunchStrip done={2} />

              <div className="mt-8 flex flex-wrap gap-5">
                <CodeSlip
                  label="Read out to open"
                  digits="4 7 2 9"
                  note="They enter this when the shift begins"
                  tone={BRAND_COLORS.accentDeep}
                />
                <CodeSlip
                  label="Read out to close"
                  digits="8 1 5 0"
                  note="They enter this when the shift ends"
                  tone={LEDGER_GREEN}
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Chapter two — SkillEmployers */}
      <section id="employers" className="anchor-target bg-black text-white">
        <div className="container mx-auto px-6 max-w-7xl py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">

            <div className="lg:col-span-5" data-reveal>
              <p className="font-ledger text-[0.6rem] tracking-[0.3em] uppercase" style={{ color: BRAND_COLORS.accent }}>
                For SkillEmployers
              </p>
              <h3 className="mt-6 font-extralight leading-[1.05] tracking-tight" style={{ fontSize: 'clamp(2rem, 4vw, 3.25rem)' }}>
                Pay only
                <span className="block mt-1" style={{ color: BRAND_COLORS.accent }}>when you need.</span>
              </h3>
              <p className="mt-6 text-base md:text-lg font-light leading-relaxed text-gray-400 max-w-md">
                Cafés, hotels, retail floors, pharmacies, multiplexes and events staff their
                busiest hours here — and stop paying the moment the rush ends.
              </p>
              <button
                onClick={() => setShowStoreDialog(true)}
                className="group mt-8 inline-flex items-center gap-3 px-7 py-4 text-[0.7rem] font-light tracking-[0.2em] uppercase transition-colors duration-200 hover:bg-white"
                style={{ backgroundColor: BRAND_COLORS.accent, color: BRAND_COLORS.primary }}
              >
                Post a requirement
                <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
              </button>
            </div>

            <ul className="lg:col-span-7 lg:pt-2" data-reveal>
              {EMPLOYER_POINTS.map((point) => (
                // Five parallel claims, not a sequence — so they are ruled apart
                // rather than numbered. Numbering here would imply an order that
                // does not exist.
                <li
                  key={point}
                  className="flex items-baseline gap-5 py-5 border-t last:border-b"
                  style={{ borderColor: HAIRLINE_WHITE }}
                >
                  <span aria-hidden="true" className="flex-none block" style={{ width: 14, height: 1, backgroundColor: BRAND_COLORS.accent, transform: 'translateY(-0.4rem)' }} />
                  <span className="text-base md:text-lg font-light leading-relaxed text-gray-300">
                    {point}
                  </span>
                </li>
              ))}
            </ul>

          </div>
        </div>
      </section>

      {/* SkillVenders in action */}
      <section id="gallery" className="anchor-target bg-white border-t" style={{ borderColor: BRAND_COLORS.lightGray }}>
        <div className="container mx-auto px-6 max-w-7xl py-14 md:py-20">

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8" data-reveal>
            <div className="max-w-2xl">
              <p className="font-ledger text-[0.65rem] tracking-[0.22em] uppercase" style={{ color: BRAND_COLORS.secondary }}>
                SkillVenders in action
              </p>
              <h3
                className="mt-6 font-light leading-[1.1] tracking-tight"
                style={{ fontSize: 'clamp(1.9rem, 3.6vw, 2.85rem)', color: BRAND_COLORS.primary }}
              >
                Real floors. Real shifts. Real pay.
              </h3>
            </div>
            <p className="font-ledger text-[0.65rem] tracking-[0.15em] uppercase" style={{ color: BRAND_COLORS.graphite }}>
              {filteredImages.length} of {GALLERY_IMAGES.length} shown
            </p>
          </div>

          {/* Filters */}
          <div className="mt-8 flex flex-wrap gap-2" data-reveal>
            {CATEGORIES.map((cat) => {
              const active = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => {
                    setActiveCategory(cat.id);
                    setLightboxIndex(null);
                  }}
                  className="font-ledger px-5 py-2.5 text-[0.6rem] tracking-[0.2em] uppercase border transition-colors duration-200"
                  style={{
                    backgroundColor: active ? BRAND_COLORS.primary : 'transparent',
                    color: active ? BRAND_COLORS.accent : BRAND_COLORS.primary,
                    borderColor: active ? BRAND_COLORS.primary : BRAND_COLORS.lightGray,
                  }}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px" style={{ backgroundColor: BRAND_COLORS.lightGray }}>
            {filteredImages.map((image, index) => (
              <button
                key={image.id}
                onClick={() => setLightboxIndex(index)}
                className="group relative block w-full overflow-hidden aspect-[4/3] bg-black text-left"
                aria-label={'Open ' + image.title}
              >
                <img
                  src={image.src}
                  alt={image.title}
                  loading="lazy"
                  className="w-full h-full object-cover grayscale-[45%] group-hover:grayscale-0 scale-105 group-hover:scale-100 transition-all duration-[1200ms] ease-out"
                />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.05) 35%, rgba(0,0,0,0.85) 100%)' }} />

                <span className="absolute top-4 right-4 p-2 border border-white/25 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <Maximize2 className="w-3.5 h-3.5 text-white" />
                </span>

                <span className="absolute inset-x-0 bottom-0 p-5">
                  <span className="font-ledger block text-[0.65rem] tracking-[0.18em] uppercase" style={{ color: BRAND_COLORS.accent }}>
                    {image.category}
                  </span>
                  <span className="block mt-2 text-base font-light text-white tracking-wide leading-snug">
                    {image.title}
                  </span>
                  <span className="block mt-1 font-ledger text-[0.62rem] tracking-[0.12em] uppercase text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {image.role} · {image.venue}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxIndex !== null && filteredImages[lightboxIndex] && (
        <div className="fixed inset-0 bg-black/95 z-50 flex flex-col justify-between select-none animate-fade-in">
          <div className="flex items-center justify-between px-6 py-4 border-b z-10" style={{ borderColor: HAIRLINE_WHITE }}>
            <div>
              <span className="font-ledger text-[0.6rem] tracking-[0.25em] uppercase" style={{ color: BRAND_COLORS.accent }}>
                SkillVend gallery
              </span>
              <p className="font-ledger text-[0.62rem] tracking-[0.15em] uppercase text-[#B3B3AD] mt-1">
                {CATEGORIES.find(c => c.id === activeCategory)?.label || 'All fields'}
              </p>
            </div>
            <button
              onClick={() => setLightboxIndex(null)}
              aria-label="Close"
              className="p-2 text-gray-400 hover:text-white border border-white/15 hover:border-white/40 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 relative flex items-center justify-center p-4 min-h-0">
            <button
              onClick={() => setLightboxIndex((prev) => (prev - 1 + filteredImages.length) % filteredImages.length)}
              aria-label="Previous"
              className="absolute left-6 p-3 text-gray-400 hover:text-white border border-white/15 hover:border-white/40 transition-colors z-10 hidden md:block"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            <div className="relative max-w-5xl max-h-[70vh] md:max-h-[75vh] w-full h-full flex items-center justify-center p-2">
              <img
                src={filteredImages[lightboxIndex].src}
                alt={filteredImages[lightboxIndex].title}
                className="max-w-full max-h-full object-contain border select-none cursor-pointer"
                style={{ borderColor: HAIRLINE_WHITE }}
                onClick={() => setLightboxIndex((prev) => (prev + 1) % filteredImages.length)}
              />
            </div>

            <button
              onClick={() => setLightboxIndex((prev) => (prev + 1) % filteredImages.length)}
              aria-label="Next"
              className="absolute right-6 p-3 text-gray-400 hover:text-white border border-white/15 hover:border-white/40 transition-colors z-10 hidden md:block"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          <div className="border-t py-6 px-6 text-center z-10" style={{ borderColor: HAIRLINE_WHITE }}>
            <div className="max-w-xl mx-auto">
              <h4 className="text-lg font-light text-white tracking-wide">
                {filteredImages[lightboxIndex].title}
              </h4>
              <p className="font-ledger mt-2 text-[0.6rem] tracking-[0.2em] uppercase" style={{ color: BRAND_COLORS.accent }}>
                {filteredImages[lightboxIndex].role} · {filteredImages[lightboxIndex].venue}
              </p>
              <p className="font-ledger text-[0.62rem] tracking-[0.2em] text-[#A8A8A2] mt-3 uppercase">
                {lightboxIndex + 1} / {filteredImages.length}
              </p>
            </div>

            <div className="flex justify-center gap-6 mt-5 md:hidden">
              <button
                onClick={() => setLightboxIndex((prev) => (prev - 1 + filteredImages.length) % filteredImages.length)}
                className="font-ledger px-4 py-2 text-[0.6rem] tracking-[0.2em] uppercase text-gray-400 border border-white/15 active:bg-white/10"
              >
                Previous
              </button>
              <button
                onClick={() => setLightboxIndex((prev) => (prev + 1) % filteredImages.length)}
                className="font-ledger px-4 py-2 text-[0.6rem] tracking-[0.2em] uppercase text-gray-400 border border-white/15 active:bg-white/10"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Frequently asked questions */}
      <FaqSection />


      {/* Closing call to action */}
      <section id="app" className="anchor-target bg-black text-white border-t" style={{ borderColor: HAIRLINE_GOLD }}>
        <div className="container mx-auto px-6 max-w-4xl py-14 md:py-20 text-center" data-reveal>
          <p className="font-ledger text-[0.6rem] tracking-[0.3em] uppercase" style={{ color: BRAND_COLORS.accent }}>
            Get the app
          </p>
          <h3 className="mt-6 font-extralight leading-[1.1] tracking-tight" style={{ fontSize: 'clamp(2rem, 4.5vw, 3.5rem)' }}>
            Your next shift is
            <span className="block mt-1" style={{ color: BRAND_COLORS.accent }}>already nearby.</span>
          </h3>
          <p className="mt-6 text-base md:text-lg font-light leading-relaxed text-gray-400 max-w-xl mx-auto">
            Register, get verified and see what is open around you. It takes a few minutes
            and costs nothing.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            {orderedStores.map((store) => (
              <StoreBadge key={store.id} store={store} className="px-8 py-4" />
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white border-t" style={{ borderColor: HAIRLINE_WHITE }}>
        <div className="container mx-auto px-6 max-w-7xl py-11">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">

            <div className="md:col-span-5">
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  if (window.location.hash) {
                    window.history.pushState(null, '', '/');
                  }
                }}
                className="flex items-center gap-4 cursor-pointer group"
                aria-label="SkillVend Home"
              >
                <BrandMark className="w-10 h-10 transition-transform group-hover:scale-105" />
                <div>
                  <p className="text-sm font-light tracking-widest" style={{ color: '#C9A961' }}>
                    SKILL<span className="font-extralight">VEND</span>
                  </p>
                  <p className="font-ledger text-[0.6rem] tracking-[0.15em] text-[#A8A8A2] mt-1 uppercase">Est. 2021 · Bengaluru</p>
                </div>
              </a>
              <p className="mt-5 text-sm font-light leading-relaxed text-[#B3B3AD] max-w-sm">
                A platform that empowers the employability of every individual by connecting
                people to real, flexible work.
              </p>
            </div>

            <div className="md:col-span-3">
              <p className="font-ledger text-[0.65rem] tracking-[0.2em] uppercase text-[#A8A8A2]">Explore</p>
              <ul className="mt-5 space-y-3">
                {NAV_LINKS.map((link) => (
                  <li key={link.href}>
                    <a href={link.href} className="text-sm font-light text-gray-300 hover:text-[#C9A961] transition-colors duration-200">
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-4">
              <p className="font-ledger text-[0.65rem] tracking-[0.2em] uppercase text-[#A8A8A2]">Company</p>
              <ul className="mt-5 space-y-3">
                <li><a href="/legal#terms" className="text-sm font-light text-gray-300 hover:text-[#C9A961] transition-colors duration-200">Terms of service</a></li>
                <li><a href="/legal#privacy" className="text-sm font-light text-gray-300 hover:text-[#C9A961] transition-colors duration-200">Privacy policy</a></li>
                <li><a href="/legal#contact" className="text-sm font-light text-gray-300 hover:text-[#C9A961] transition-colors duration-200">Contact us</a></li>
                <li><a href="mailto:helpdesk@skillvend.com" className="text-sm font-light text-gray-300 hover:text-[#C9A961] transition-colors duration-200">helpdesk@skillvend.com</a></li>
              </ul>
            </div>
          </div>

          <div className="mt-9 pt-6 border-t flex flex-col sm:flex-row justify-between gap-3" style={{ borderColor: HAIRLINE_WHITE }}>
            <p className="font-ledger text-[0.62rem] tracking-[0.15em] uppercase text-[#A8A8A2]">
              © 2021–2026 SkillVend. All rights reserved.
            </p>
            <p className="font-ledger text-[0.62rem] tracking-[0.15em] uppercase text-[#A8A8A2]">
              Empowering employability
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
